<template>
  <scrollable-container>
    <placeholder
      v-show="isTableAwaiting"
      :text="text"
      @buttonClick="buildReport"
    />
    <loader v-show="isTableLoading" />
    <container
      v-show="isTableReady"
      id="printable"
      :class="[{ [$style.message]: isTableAwaiting }, $style.scrollableContent]"
    >
      <div
        v-if="tableData.length === 0"
        :class="$style.emptyResult"
      >
        {{ $t('reports.noDataForSpecifiedPeriod') }}
      </div>

      <activity-table
        v-for="user in paginationUsers"
        :key="user.name"
        :class="$style.name"
        :user="user"
      />
      <paginate
        v-if="!isPdfPage && tableData.length > perUser"
        :page-count="countUser"
        :click-handler="(page) => setUserPage(page)"
        :prev-text="'‹'"
        :next-text="'›'"
        :page="userPage"
        :container-class="$style.pagination"
        :page-class="$style.page2"
        :prev-class="$style.page2"
        :next-class="$style.page2"
        :active-class="$style.pageActive2"
      />
    </container>
  </scrollable-container>
</template>

<script>
  import ScrollableContainer from '@/components/common/blocks/scrollable-container.vue';
  import Container from '@/components/common/blocks/container.vue';
  import Placeholder from '@/components/common/blocks/report-placeholder.vue';
  import { getActivityFeed } from '@/api/methods/reports/users/index';
  import Loader from '@/components/common/blocks/loader.vue';
  import { getDataField } from '@/views/reports/users/worktime/table-field';
  import { getTime } from '@/helpers/format-date';
  import { REPORT_ENUM } from '@/assets/scripts/reportEnum';
  import Paginate from 'vuejs-paginate';
  import ActivityTable from './activity-table.vue';

  export default {
    components: {
      ScrollableContainer,
      Container,
      Placeholder,
      ActivityTable,
      Loader,
      Paginate,
    },
    data() {
      return {
        name: this.$t('reports.activityFeed'),
        tableData: [],
        computersArray: [],
        text: this.$t('reports.productivityReport'),
        columns: [
          { name: this.$t('reports.columns.time'), field: 'eventTime' },
          { name: this.$t('reports.columns.path'), field: 'path' },
          { name: this.$t('reports.columns.description'), field: 'action' },
          { name: this.$t('reports.columns.windowTitle'), field: 'caption' },
          { name: this.$t('reports.columns.activeTime'), field: 'activeTime' },
          { name: this.$t('reports.columns.category'), field: 'group' },
          { name: this.$t('reports.columns.productivity'), field: 'productivity' },
        ],

        perUser: 1,
        userPage: 1,
      };
    },
    computed: {
      isPdfPage() {
        return this.$route.query.pdf === 'true';
      },
      isTableReady() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_READY;
      },
      isTableAwaiting() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.REQUEST_AWAITING;
      },
      isTableLoading() {
        return this.$store.getters['report/getStateTable'] === REPORT_ENUM.TABLE_LOADING;
      },
      dateRange() {
        return this.$store.getters['filters/formattedDateRange'];
      },
      checkedComputers() {
        return this.$store.getters['pageSpecificData/checkedComputersList'];
      },
      computersTree() {
        const tree = this.$store.getters['pageSpecificData/computersTree'];
        return tree;
      },
      countUser() {
        return Math.ceil(this.tableData.length / this.perUser);
      },

      paginationUsers() {
        if (this.isPdfPage || this.tableData.length === 1) {
          return this.tableData;
        }
        const start = (this.userPage - 1) * this.perUser;
        const end = start + this.perUser;
        return this.tableData.slice(start, end);
      },
    },
    watch: {
      dateRange(oldValue, newValue) {
        if (
          oldValue.dateFrom.toString() !== newValue.dateFrom.toString() ||
          oldValue.dateTo.toString() !== newValue.dateTo.toString()
        ) {
          this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
        }
      },
      checkedComputers(oldArray, newArray) {
        if (oldArray !== newArray) {
          this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
        }
      },
      computersTree: {
        immediate: false,
        async handler(newVal) {
          if (!newVal) return;

          if (this.isPdfPage && newVal.length) {
            await this.buildReport();
            await this.$nextTick();

            await new Promise((resolve) => {
              setTimeout(resolve, 2000);
            });
            // eslint-disable-next-line no-underscore-dangle
            window.__PDF_READY__ = true;
          }

          if (this.$route.query?.isLoading) {
            this.buildReport();
          }
        },
      },
    },
    mounted() {
      this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
    },
    methods: {
      getCheckedComputersFromTree(node) {
        if (node.type === 'computer' && node.checked) {
          this.computersArray.push(node);
        }
        if (node.type === 'folder') {
          node.children.forEach((child) => this.getCheckedComputersFromTree(child));
        }
      },
      buildReport() {
        this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_LOADING);
        this.tableData = [];
        this.computersArray = [];

        const compInStorage = JSON.parse(localStorage.getItem('selectedComputers'));

        if (this.isPdfPage && compInStorage?.length) {
          this.computersArray = compInStorage;
        } else if (this.computersTree) {
          this.computersTree.forEach((node) => this.getCheckedComputersFromTree(node));
        }

        if (this.dateRange.dateFrom && this.dateRange.dateTo && this.computersArray) {
          this.promise = getActivityFeed(this.computersArray, this.dateRange.dateFrom, this.dateRange.dateTo).then(
            ({ data }) => {
              if (data) {
                this.tableData = data.result;

                this.$store.commit('report/setStateTable', REPORT_ENUM.TABLE_READY);

                const exportArray = [];

                for (let i = 0; i < data.result.length; i++) {
                  const { name } = data.result[i];
                  for (let x = 0; x < data.result[i].dates.length; x++) {
                    const { date } = data.result[i].dates[x];

                    for (let y = 0; y < data.result[i].dates[x].events.length; y++) {
                      const resObj = { Пользователь: name, Дата: date };
                      for (let j = 0; j < this.columns.length; j++) {
                        resObj[this.columns[j].name] = getDataField(
                          data.result[i].dates[x].events[y][this.columns[j].field],
                          this.columns[j].field,
                        );
                        if (this.columns[j].field === 'eventTime') {
                          resObj[this.columns[j].name] = getTime(
                            data.result[i].dates[x].events[y][this.columns[j].field],
                          );
                        }
                      }
                      exportArray.push(resObj);
                    }
                  }
                }

                this.$store.commit('exportData/setActivityFeed', { name: this.name, data: exportArray });
              } else {
                this.$store.commit('report/setStateTable', REPORT_ENUM.REQUEST_AWAITING);
              }
            },
          );
        }
        return this.tableData;
      },
      setUserPage(page) {
        this.userPage = page;
      },
    },
  };
</script>

<style lang="scss" module>
  .pagination {
    display: flex;
    justify-content: center;
    gap: 6px;
    margin: 10px 0;
  }

  .page2 {
    cursor: pointer;
    padding: 4px;
  }
  .pageActive2 {
    background-color: #2985bf;
    color: #fff;
  }

  ::-webkit-scrollbar {
    width: 4px;
  }

  ::-webkit-scrollbar-track {
    background: white;
  }

  ::-webkit-scrollbar-thumb {
    background: $branchColor;
    border-radius: 2px;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: darkgray;
  }

  .scrollableContent {
    margin: 0 8px 0 0;
    padding: 0 12px 0 20px;
    box-shadow: $shadow;
    flex: 1;
  }

  .message {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .emptyResult {
    display: flex;
    justify-content: center;
    margin-top: 20px;
  }
</style>
